import type { paths } from "@/api-schema";
import type { BaseSorting } from "@/components/data-table/predefined/_components/base-predefined-table.types";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const COMPANY_GROUP_SEARCH_RESOURCE = "company-group" as const;
export const COMPANY_GROUP_SEARCH_OPERATION = "search" as const;
export const COMPANY_GROUP_SEARCH_API_PATH =
  `/v1/${COMPANY_GROUP_SEARCH_RESOURCE}/${COMPANY_GROUP_SEARCH_OPERATION}` as const;

type CompanyGroupSearchParametersSchema = paths[typeof COMPANY_GROUP_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type CompanyGroupSearchParameters = Omit<NonNullable<CompanyGroupSearchParametersSchema>, "orderBy"> & {
  orderBy?: BaseSorting;
};
export type CompanyGroupSearchBody =
  paths[typeof COMPANY_GROUP_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type CompanyGroupSearchResponse =
  paths[typeof COMPANY_GROUP_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useCompanyGroupSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<CompanyGroupSearchParameters, CompanyGroupSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    CompanyGroupSearchResponse,
    Error,
    CompanyGroupSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<CompanyGroupSearchParameters, CompanyGroupSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      COMPANY_GROUP_SEARCH_RESOURCE,
      COMPANY_GROUP_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<CompanyGroupSearchResponse>(
        COMPANY_GROUP_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
